import type { Plugin } from '@/shared/types/plugin'
import type { VideoRenderDiagnostics, VideoRenderPresetDiagnostics } from './types'

export const VIDEO_RENDER_DEFAULT_PLUGIN_NAMES = [
  'ViewPlugin',
  'CameraPlugin',
  'RenderPlugin',
  'AnimationPlugin',
  'PickingPlugin',
  'GaussianSplatPlugin',
  'VideoRenderPlugin',
] as const

export const VIDEO_RENDER_EXCLUDED_PLUGIN_NAMES = [
  'InputPlugin',
  'ControlPlugin',
  'MeasurePlugin',
  'SelectionPlugin',
  'ScreenBoxSelectPlugin',
  'MeasureOverlayPlugin',
  'ClipOverlayPlugin',
  'VolumeOverlayPlugin',
  'ProfileOverlayPlugin',
  'ScreenBoxSelectOverlayPlugin',
  'BoundingBoxOverlayPlugin',
  'StatsPlugin',
] as const

export function getPluginDiagnosticName(plugin: Plugin): string {
  const ctorName = plugin.constructor?.name
  return ctorName && ctorName !== 'Object' ? ctorName : plugin.name
}

export function createVideoRenderPresetDiagnostics(
  additionalPlugins: Plugin[] = []
): VideoRenderPresetDiagnostics {
  return {
    name: 'videoRender',
    defaultPluginNames: [...VIDEO_RENDER_DEFAULT_PLUGIN_NAMES],
    additionalPluginNames: additionalPlugins.map(getPluginDiagnosticName),
    excludedPluginNames: [...VIDEO_RENDER_EXCLUDED_PLUGIN_NAMES],
  }
}

export function sanitizeVideoRenderDiagnostics(
  diagnostics: VideoRenderDiagnostics
): VideoRenderDiagnostics {
  return sanitizeValue(diagnostics) as VideoRenderDiagnostics
}

function sanitizeValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return sanitizeDiagnosticString(value)
  }
  if (Array.isArray(value)) {
    return value.map(sanitizeValue)
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, sanitizeValue(child)])
    )
  }
  return value
}

function sanitizeDiagnosticString(value: string): string {
  return redactLocalPaths(redactSensitiveAssignments(redactSensitiveUrlQueries(value)))
}

function redactSensitiveUrlQueries(value: string): string {
  return value.replace(/\bhttps?:\/\/[^\s"'<>]+/gi, (rawUrl) => {
    try {
      const url = new URL(rawUrl)
      url.search = ''
      url.hash = ''
      return url.toString()
    } catch {
      return rawUrl.replace(/\?[^#\s"'<>]*/g, '?[redacted]')
    }
  })
}

function redactSensitiveAssignments(value: string): string {
  return value.replace(
    /\b(token|access_token|signature|sig|cookie|authorization)=([^&\s]+)/gi,
    '$1=[redacted]'
  )
}

function redactLocalPaths(value: string): string {
  return value
    .replace(/\b[A-Za-z]:\\(?:[^\\/:*?"<>|\r\n]+\\)+([^\\/:*?"<>|\r\n]+)/g, '[local-path]\\$1')
    .replace(/(^|\s)\/(?:[^/\s]+\/)+([^/\s]+)/g, '$1[local-path]/$2')
}
